import { editor as MonacoEditor, Range } from 'monaco-editor';
import type { FrameworkArgument } from '../../../framework/framework.d';
import {
  getFrameworkAxisActionAlternation,
  getFrameworkAxisInputArgAlternation,
  getInputArgNameForAxisAction,
} from '../../../framework/framework-axis-actions';
import type { InputBinding, InputBindingAction, SingleActionInputBinding } from '../../../utils/inputBindings';
import {
  dedupeInputBindingsBySignature,
  getUnsupportedInputBindingActionExpression,
  INPUT_BINDING_KIND,
  inputBindingSignature,
  isUnsupportedInputBinding,
} from '../../../utils/inputBindings';
import {
  clampInsertLineBeforeRobotStart,
  escapeRegex,
  findOrCreateRegionBlock,
  findRobotStartLine,
  getInsertLineBeforeRobotStart,
  getLastMappingScanLineBeforeRobotStart,
  removeEmptyInputMappingsRegionIfNeeded,
} from '../../../utils/monacoRegionUtils';
import { formatPythonActionArgument, formatPythonScalar, shouldQuotePythonActionArg } from '../../../utils/pythonActionFormatting';
import { formatGeneratedPythonCall } from '../../../utils/pythonFormattingUtils';
import {
  bumpInsertLineForTopLevelVarDeclarationAnchors,
  collectLastTopLevelVarAssignmentsByName,
  findConstructorCallEndLineIndex,
  findBalancedCallLineRanges,
  findFirstUnmatchedOpenParenIndex,
  findTopLevelVarDeclarationEndLine,
  getTextModelLines,
  isTopLevelCodeLine,
  leadingWhitespaceLength,
  VAR_ASSIGN_RE,
  type TopLevelVarDeclarationAnchor,
} from '../../../utils/pythonSourceScanUtils';
import { getFrameworkActionTargetAllowedClassNames } from '../../../utils/frameworkClassNameSets';
import { INPUT_MAPPINGS_REGION_START, REGION_END } from '../../../utils/programRegionMarkers';
import { getMainProgramEditorProxy } from '../../CodeEditor/utils/mainProgramEditor';
import {
  shouldPreserveRawDriveUnitPortArgument,
  shouldPreserveRawFrameworkEnumArgument,
  shouldPreserveRawFrameworkNumericArgument,
  shouldPreserveRawFrameworkStringArgument,
} from '../../../utils/frameworkArgTypes';
import { isSequenceTupleArg, isTupleArg, readSequenceTupleMemberValue, readSequenceTupleRows, readTupleMemberValue } from '../../../utils/tupleArg';

const AXIS_BUTTON_TO_GETTER: Record<string, string> = {
  r1: 'get_r1_axis',
  l1: 'get_l1_axis',
  r2: 'get_r2_axis',
  l2: 'get_l2_axis',
};

export type CodeMutationResult =
  | {
      ok: true;
      changed: boolean;
    }
  | {
      ok: false;
      reason: string;
    };

interface BindingSourceRange {
  startLine: number;
  endLine: number;
}

function getAxisGetterForButton(button: string): string | null {
  return AXIS_BUTTON_TO_GETTER[String(button || '').toLowerCase()] ?? null;
}

function escapeButtonAccessor(button: string): string {
  return escapeRegex(String(button || '').toLowerCase());
}

function shouldPreserveRawFrameworkArgument(arg: FrameworkArgument, value: unknown, actionName?: string): boolean {
  return (
    shouldPreserveRawDriveUnitPortArgument(arg, value) ||
    shouldPreserveRawFrameworkNumericArgument(arg, value) ||
    shouldPreserveRawFrameworkEnumArgument(arg, value) ||
    shouldPreserveRawFrameworkStringArgument(arg, value, actionName, arg.rawSourceExpression)
  );
}

function formatInputBindingTupleMembers(
  members: FrameworkArgument[],
  getMemberValue: (member: FrameworkArgument, index: number) => unknown,
  actionName: string
): string {
  const formattedMembers = members.map((member, index) => {
    const memberValue = getMemberValue(member, index) ?? member.defaultValue;
    if (shouldPreserveRawFrameworkArgument(member, memberValue, actionName)) {
      return String(member.rawSourceExpression || memberValue).trim();
    }
    return formatPythonScalar(memberValue, shouldQuotePythonActionArg({ name: member.name, type: member.type }, actionName));
  });
  return `(${formattedMembers.join(', ')})`;
}

function formatInputBindingTupleValue(arg: FrameworkArgument, actionName: string): string {
  return formatInputBindingTupleMembers(arg.members || [], (_member, index) => readTupleMemberValue(arg, index, _member.name), actionName);
}

function formatInputBindingSequenceTupleValue(arg: FrameworkArgument, actionName: string): string {
  const members = arg.members || [];
  const rows = readSequenceTupleRows(arg).map((row) =>
    formatInputBindingTupleMembers(members, (member, index) => readSequenceTupleMemberValue(row, index, member.name), actionName)
  );
  return `[${rows.join(', ')}]`;
}

function formatInputBindingActionArgument(arg: FrameworkArgument, actionName?: string): string {
  if (isSequenceTupleArg(arg)) {
    if (Array.isArray(arg.value)) {
      return `${arg.name}=${formatInputBindingSequenceTupleValue(arg, String(actionName || ''))}`;
    }
    // Preserved unsupported expression (e.g. `get_lines()`) — emit verbatim rather than quoting it.
    const rawValue = String(arg.value ?? '').trim();
    if (rawValue) return `${arg.name}=${rawValue}`;
    return `${arg.name}=[]`;
  }
  if (isTupleArg(arg)) {
    if (arg.rawSourceExpression) return `${arg.name}=${String(arg.rawSourceExpression).trim()}`;
    return `${arg.name}=${formatInputBindingTupleValue(arg, String(actionName || ''))}`;
  }
  if (shouldPreserveRawFrameworkArgument(arg, arg.value, actionName)) {
    return `${arg.name}=${String(arg.rawSourceExpression || arg.value).trim()}`;
  }
  return formatPythonActionArgument(arg, actionName);
}

function buildActionQualified(bindingAction: InputBindingAction): { isCustomAction: boolean; actionQualified: string } {
  const isCustomAction = String(bindingAction.parentName || '').toLowerCase() === 'custom' && !bindingAction.mechanismName;
  const hasActionName = String(bindingAction.action?.name || '').trim().length > 0;
  const actionParts = [bindingAction.parentName, bindingAction.mechanismName, bindingAction.action.name].filter(Boolean);
  const actionQualified = hasActionName
    ? isCustomAction
      ? String(bindingAction.action.name || '')
      : actionParts.filter((part, index) => part !== actionParts[index - 1]).join('.')
    : '';
  return { isCustomAction, actionQualified };
}

function buildActionCall(bindingAction: InputBindingAction): string | null {
  const unsupportedExpression = getUnsupportedInputBindingActionExpression(bindingAction);
  if (unsupportedExpression) return unsupportedExpression;

  const { isCustomAction, actionQualified } = buildActionQualified(bindingAction);
  if (!actionQualified) return null;
  if (isCustomAction) return actionQualified;
  const pythonArgs = (bindingAction.action.arguments || [])
    .map((arg: FrameworkArgument) => formatInputBindingActionArgument(arg, bindingAction.action.name))
    .join(', ');
  return `${actionQualified}(${pythonArgs})`;
}

function buildControllerAnyAxisMappingRegex(controllerVarName: string): RegExp {
  const axisAlt = getFrameworkAxisActionAlternation();
  const inputArgAlt = getFrameworkAxisInputArgAlternation();
  return new RegExp(
    String.raw`^\s*[A-Za-z_][A-Za-z0-9_]*(?:\.[A-Za-z_][A-Za-z0-9_]*)*\s*\.\s*${axisAlt}\s*\(\s*${inputArgAlt}\s*=\s*${escapeRegex(
      controllerVarName
    )}\.(get_r1_axis|get_l1_axis|get_r2_axis|get_l2_axis)\s*(?:,|\))`
  );
}

function buildControllerAxisMappingRegex(controllerVarName: string, getter: string): RegExp {
  const axisAlt = getFrameworkAxisActionAlternation();
  const inputArgAlt = getFrameworkAxisInputArgAlternation();
  return new RegExp(
    String.raw`^\s*[A-Za-z_][A-Za-z0-9_]*(?:\.[A-Za-z_][A-Za-z0-9_]*)*\s*\.\s*${axisAlt}\s*\(\s*${inputArgAlt}\s*=\s*${escapeRegex(
      controllerVarName
    )}\.${getter}\s*(?:,|\))`
  );
}

function buildAxisInputControlLine(controllerVarName: string, binding: SingleActionInputBinding): string | null {
  const getter = getAxisGetterForButton(binding.button);
  if (!getter) return null;
  const actionCallTarget = buildActionQualified(binding.action).actionQualified;
  if (!actionCallTarget) return null;
  const actionName = binding.action.action.name;
  const inputArgName = getInputArgNameForAxisAction(actionName) ?? 'variable_input';
  const args = (binding.action.action.arguments || [])
    .filter((arg: FrameworkArgument) => String(arg?.name || '') !== inputArgName)
    .map((arg: FrameworkArgument) => formatInputBindingActionArgument(arg, actionName))
    .join(', ');
  return `${actionCallTarget}(${inputArgName}=${controllerVarName}.${getter}${args ? `, ${args}` : ''})`;
}

function bindingMethod(binding: SingleActionInputBinding): 'on_pressed' | 'on_released' | null {
  if (binding.kind === INPUT_BINDING_KIND.PRESSED) return 'on_pressed';
  if (binding.kind === INPUT_BINDING_KIND.RELEASED) return 'on_released';
  return null;
}

function buildDirectBindingLine(varName: string, binding: InputBinding): string | null {
  if (isUnsupportedInputBinding(binding)) return null;
  if (binding.kind === INPUT_BINDING_KIND.TOGGLE) {
    const onActionCall = buildActionCall(binding.onAction);
    const offActionCall = buildActionCall(binding.offAction);
    if (!onActionCall || !offActionCall) return null;
    return `${varName}.${binding.button}().on_toggle(${onActionCall}, ${offActionCall})`;
  }
  if (binding.kind === INPUT_BINDING_KIND.VARIABLE) return buildAxisInputControlLine(varName, binding);
  const method = bindingMethod(binding);
  const actionCall = buildActionCall(binding.action);
  if (!method || !actionCall) return null;
  return `${varName}.${binding.button}().${method}(${actionCall})`;
}

function buildFormattedDirectBindingLine(varName: string, binding: InputBinding): string | null {
  const line = buildDirectBindingLine(varName, binding);
  return line ? formatGeneratedPythonCall(line) : null;
}

function buildDirectBindingRegex(varName: string, binding: InputBinding): RegExp | null {
  if (isUnsupportedInputBinding(binding)) return null;
  if (binding.kind === INPUT_BINDING_KIND.VARIABLE) {
    const getter = getAxisGetterForButton(binding.button);
    return getter ? buildControllerAxisMappingRegex(varName, getter) : null;
  }
  const method = binding.kind === INPUT_BINDING_KIND.TOGGLE ? 'on_toggle' : bindingMethod(binding);
  if (!method) return null;
  return new RegExp(String.raw`^\s*${escapeRegex(varName)}\.${escapeButtonAccessor(binding.button)}\(\)\s*\.\s*${method}\s*\(`);
}

function buildSemanticBindingRegex(varName: string, binding: InputBinding): RegExp | null {
  if (isUnsupportedInputBinding(binding)) return null;
  if (binding.kind === INPUT_BINDING_KIND.TOGGLE) {
    const onActionQualified = buildActionQualified(binding.onAction).actionQualified;
    const offActionQualified = buildActionQualified(binding.offAction).actionQualified;
    if (!onActionQualified || !offActionQualified) return null;
    return new RegExp(
      String.raw`^\s*${escapeRegex(varName)}\.${escapeButtonAccessor(binding.button)}\(\)\s*\.\s*on_toggle\s*\(\s*${escapeRegex(
        onActionQualified
      )}\s*(?:\(|,)\s*.*,\s*${escapeRegex(offActionQualified)}\s*(?:\(|\))`
    );
  }

  const actionQualified = buildActionQualified(binding.action).actionQualified;
  if (!actionQualified) return null;

  if (binding.kind === INPUT_BINDING_KIND.VARIABLE) {
    const getter = getAxisGetterForButton(binding.button);
    if (!getter) return null;
    const inputArgAlt = getFrameworkAxisInputArgAlternation();
    return new RegExp(String.raw`^\s*${escapeRegex(actionQualified)}\s*\([^#]*\b${inputArgAlt}\s*=\s*${escapeRegex(varName)}\.${getter}\s*(?:,|\))`);
  }

  const method = bindingMethod(binding);
  if (!method) return null;
  return new RegExp(
    String.raw`^\s*${escapeRegex(varName)}\.${escapeButtonAccessor(binding.button)}\(\)\s*\.\s*${method}\s*\(\s*${escapeRegex(
      actionQualified
    )}\s*(?:\(|\))`
  );
}

function getActionTargetDeclarationName(action: InputBindingAction): string | null {
  const parentName = String(action.parentName || '').trim();
  if (!parentName || parentName.toLowerCase() === 'custom' || parentName === 'UtilityActions') return null;
  const mechanismName = String(action.mechanismName || '').trim();
  if (mechanismName) return mechanismName;
  return parentName;
}

type ActionTargetDeclarationAnchor = TopLevelVarDeclarationAnchor;

function getActionTargetDeclarationAnchor(action: InputBindingAction): ActionTargetDeclarationAnchor | null {
  const variableName = getActionTargetDeclarationName(action);
  if (!variableName) return null;
  return {
    variableName,
    allowedClassNames: getFrameworkActionTargetAllowedClassNames(action.mechanismClass?.name ?? action.parentClass?.name),
  };
}

function collectActionTargetDeclarationAnchors(binding: InputBinding): ActionTargetDeclarationAnchor[] {
  if (isUnsupportedInputBinding(binding)) return [];
  const anchors = new Map<string, ActionTargetDeclarationAnchor>();
  const addActionTarget = (action: InputBindingAction) => {
    const anchor = getActionTargetDeclarationAnchor(action);
    if (!anchor) return;
    const existing = anchors.get(anchor.variableName);
    if (!existing?.allowedClassNames || anchor.allowedClassNames) {
      anchors.set(anchor.variableName, anchor);
    }
  };
  if (binding.kind === INPUT_BINDING_KIND.TOGGLE) {
    addActionTarget(binding.onAction);
    addActionTarget(binding.offAction);
    return Array.from(anchors.values());
  }
  addActionTarget(binding.action);
  return Array.from(anchors.values());
}

function getInsertLineAfterEffectiveInputPanelDeclaration(
  model: MonacoEditor.ITextModel,
  className: 'ActionController' | 'ActionDriveBasePanel',
  defaultVarName: string,
  fallbackLine: number
): number {
  const variableName = resolveEffectiveInputPanelVarName(model, className, defaultVarName);
  const declEndLine = findTopLevelVarDeclarationEndLine(model, variableName, {
    allowedClassNames: new Set([className]),
  });
  return declEndLine !== null ? declEndLine + 1 : fallbackLine;
}

function hasTopLevelInputPanelDeclaration(model: MonacoEditor.ITextModel, className: 'ActionController' | 'ActionDriveBasePanel'): boolean {
  const lines = getTextModelLines(model);
  return (
    collectLastTopLevelVarAssignmentsByName(lines, {
      allowedClassNames: new Set([className]),
    }).length > 0
  );
}

function resolveEffectiveInputPanelVarName(
  model: MonacoEditor.ITextModel,
  className: 'ActionController' | 'ActionDriveBasePanel',
  defaultName: string
): string {
  const lines = getTextModelLines(model);
  const assignments = collectLastTopLevelVarAssignmentsByName(lines, {
    allowedClassNames: new Set([className]),
  });
  if (assignments.length === 0) return defaultName;
  assignments.sort((left, right) => left.lineIndex - right.lineIndex);
  return assignments[0]!.variableName;
}

export function ensureControllerExists(editor: MonacoEditor.IStandaloneCodeEditor) {
  const targetEditor = getMainProgramEditorProxy(editor) ?? editor;
  const model = targetEditor.getModel();
  if (!model) return;
  const hasAnyController = hasTopLevelInputPanelDeclaration(model, 'ActionController');
  if (!hasAnyController) {
    const insertLine = getInsertLineBeforeRobotStart(model) ?? model.getLineCount() + 1;
    const { startLine } = findOrCreateRegionBlock(targetEditor, {
      startMarker: INPUT_MAPPINGS_REGION_START,
      endMarker: REGION_END,
      insertLine,
    });
    const assignmentLine = startLine + 1;
    targetEditor.pushUndoStop();
    targetEditor.executeEdits('controller.ensure', [
      { range: new Range(assignmentLine, 1, assignmentLine, 1), text: 'controller = ActionController()\n', forceMoveMarkers: true },
    ]);
    targetEditor.pushUndoStop();
    targetEditor.focus();
  }
}

export function ensureDrivebasePanelExists(editor: MonacoEditor.IStandaloneCodeEditor) {
  const targetEditor = getMainProgramEditorProxy(editor) ?? editor;
  const model = targetEditor.getModel();
  if (!model) return;
  const hasAny = hasTopLevelInputPanelDeclaration(model, 'ActionDriveBasePanel');
  if (!hasAny) {
    const insertLine = getInsertLineBeforeRobotStart(model) ?? model.getLineCount() + 1;
    const { startLine } = findOrCreateRegionBlock(targetEditor, {
      startMarker: INPUT_MAPPINGS_REGION_START,
      endMarker: REGION_END,
      insertLine,
    });
    const assignmentLine = getInsertLineAfterEffectiveInputPanelDeclaration(
      targetEditor.getModel() ?? model,
      'ActionController',
      'controller',
      startLine + 1
    );
    targetEditor.pushUndoStop();
    targetEditor.executeEdits('drivebase.ensure', [
      {
        range: new Range(assignmentLine, 1, assignmentLine, 1),
        text: 'drivebase_panel = ActionDriveBasePanel()\n',
        forceMoveMarkers: true,
      },
    ]);
    targetEditor.pushUndoStop();
    targetEditor.focus();
  }
}

export function resolveControllerVarName(model: MonacoEditor.ITextModel): string {
  return resolveEffectiveInputPanelVarName(model, 'ActionController', 'controller');
}

export function resolveDrivebasePanelVarName(model: MonacoEditor.ITextModel): string {
  return resolveEffectiveInputPanelVarName(model, 'ActionDriveBasePanel', 'drivebase_panel');
}

function anyDirectMappingRegex(varName: string): RegExp {
  return new RegExp(String.raw`^\s*${escapeRegex(varName)}\.[A-Za-z_][A-Za-z0-9_]*\(\)\s*\.\s*(on_pressed|on_released|on_change|on_toggle)\s*\(`);
}

function controllerAxisGetterRegex(varName: string): RegExp {
  return new RegExp(String.raw`\b${escapeRegex(varName)}\.(get_r1_axis|get_l1_axis|get_r2_axis|get_l2_axis)\b`);
}

function inputPanelClassName(device: InputBinding['device']): 'ActionController' | 'ActionDriveBasePanel' {
  return device === 'drivebase' ? 'ActionDriveBasePanel' : 'ActionController';
}

function getInputPanelDeclarationEndLine(model: MonacoEditor.ITextModel, varName: string, device: InputBinding['device']): number | null {
  return findTopLevelVarDeclarationEndLine(model, varName, {
    allowedClassNames: new Set([inputPanelClassName(device)]),
  });
}

function findFormattedAxisMappingEndLine(model: MonacoEditor.ITextModel, varName: string, startLine: number): number | null {
  const lines = getTextModelLines(model);
  const startLineIndex = startLine - 1;
  const axisGetterRe = controllerAxisGetterRegex(varName);
  const [range] = findBalancedCallLineRanges(lines, (_line, lineIndex) => lineIndex === startLineIndex, findFirstUnmatchedOpenParenIndex);

  if (!range || range.endLineIndex <= range.startLineIndex) return null;
  const sourceSpan = lines.slice(range.startLineIndex, range.endLineIndex + 1).join('\n');
  return axisGetterRe.test(sourceSpan) ? range.endLineIndex + 1 : null;
}

function findFormattedDirectMappingEndLine(model: MonacoEditor.ITextModel, varName: string, startLine: number): number | null {
  const line = model.getLineContent(startLine);
  if (!anyDirectMappingRegex(varName).test(line)) return null;
  const lines = getTextModelLines(model);
  const startLineIndex = startLine - 1;
  const [range] = findBalancedCallLineRanges(lines, (_line, lineIndex) => lineIndex === startLineIndex, findFirstUnmatchedOpenParenIndex);

  return range && range.endLineIndex > range.startLineIndex ? range.endLineIndex + 1 : null;
}

function getInsertLineForInputPanelMappingsBlock(model: MonacoEditor.ITextModel, varName: string, device: InputBinding['device']): number {
  const insertBeforeStart = getInsertLineBeforeRobotStart(model);
  if (insertBeforeStart !== null) return insertBeforeStart;
  const declEndLine = getInputPanelDeclarationEndLine(model, varName, device);
  if (declEndLine !== null) return declEndLine;
  return model.getLineCount() + 1;
}

/** 1-based line for the next input-panel mapping, always after its declaration (even outside the managed region). */
function getInsertLineForInputPanelBinding(
  model: MonacoEditor.ITextModel,
  varName: string,
  device: InputBinding['device'],
  binding?: InputBinding
): number {
  const declEndLine = getInputPanelDeclarationEndLine(model, varName, device);
  if (declEndLine === null) return getInsertLineForInputPanelMappingsBlock(model, varName, device);

  const mappingRe = anyDirectMappingRegex(varName);
  const axisControllerVarName = device === 'controller' ? varName : resolveControllerVarName(model);
  const axisMappingRe = buildControllerAnyAxisMappingRegex(axisControllerVarName);
  const maxScanLine = getLastMappingScanLineBeforeRobotStart(model);
  let lastMappingLine = declEndLine;
  for (let i = declEndLine + 1; i <= maxScanLine; i++) {
    const line = model.getLineContent(i);
    if (!isTopLevelCodeLine(line)) continue;
    if (mappingRe.test(line)) {
      const formattedDirectMappingEndLine = findFormattedDirectMappingEndLine(model, varName, i);
      if (formattedDirectMappingEndLine !== null) {
        lastMappingLine = formattedDirectMappingEndLine;
        i = formattedDirectMappingEndLine;
        continue;
      }
      lastMappingLine = i;
      continue;
    }
    if (axisMappingRe.test(line)) {
      lastMappingLine = i;
      continue;
    }
    const formattedAxisMappingEndLine = findFormattedAxisMappingEndLine(model, axisControllerVarName, i);
    if (formattedAxisMappingEndLine !== null) {
      lastMappingLine = formattedAxisMappingEndLine;
      i = formattedAxisMappingEndLine;
    }
  }
  let insertLine = lastMappingLine + 1;
  if (binding) {
    insertLine = bumpInsertLineForTopLevelVarDeclarationAnchors(model, insertLine, collectActionTargetDeclarationAnchors(binding));
  }
  return clampInsertLineBeforeRobotStart(model, insertLine);
}

function getMaxDeclaredActionTargetInsertLine(model: MonacoEditor.ITextModel, binding: InputBinding): number {
  return bumpInsertLineForTopLevelVarDeclarationAnchors(model, 0, collectActionTargetDeclarationAnchors(binding));
}

function bindingLineNeedsRelocation(model: MonacoEditor.ITextModel, bindingLine: number, binding: InputBinding): boolean {
  const startLine = findRobotStartLine(model);
  if (startLine !== null && bindingLine >= startLine) return true;
  const actionTargetInsertLine = getMaxDeclaredActionTargetInsertLine(model, binding);
  return actionTargetInsertLine > 0 && actionTargetInsertLine > bindingLine;
}

function matchUnsupportedRawSpan(model: MonacoEditor.ITextModel, binding: InputBinding): BindingSourceRange | null {
  if (!isUnsupportedInputBinding(binding)) return null;
  const startLine = binding.sourceLineNumber;
  if (startLine === undefined || startLine < 1 || startLine > model.getLineCount()) return null;
  const rawLines = String(binding.rawCode || '').split(/\r?\n/);
  const endLine = startLine + rawLines.length - 1;
  if (endLine > model.getLineCount()) return null;
  const sourceSpan = rawLines.map((_, index) => model.getLineContent(startLine + index)).join('\n');
  return sourceSpan.trim() === String(binding.rawCode || '').trim() ? { startLine, endLine } : null;
}

function collectMatchingLineNumbers(model: MonacoEditor.ITextModel, varName: string, binding: InputBinding): number[] {
  const expectedLine = buildDirectBindingLine(varName, binding);
  if (expectedLine) {
    const exactMatches: number[] = [];
    for (let i = 1; i <= model.getLineCount(); i++) {
      if (model.getLineContent(i).trim() === expectedLine) exactMatches.push(i);
    }
    if (exactMatches.length > 0) return exactMatches;
  }

  const fallbackRegex = buildDirectBindingRegex(varName, binding);
  if (!fallbackRegex) return [];
  const semanticRegex = buildSemanticBindingRegex(varName, binding);
  const matches: number[] = [];
  for (let i = 1; i <= model.getLineCount(); i++) {
    const line = model.getLineContent(i);
    if (fallbackRegex.test(line) && (!semanticRegex || semanticRegex.test(line))) matches.push(i);
  }
  return matches;
}

function applyLeadingIndentFromLine(sourceLine: string, unindentedLine: string): string {
  const match = /^(\s*)/.exec(sourceLine);
  const indent = match?.[1] ?? '';
  return indent
    ? unindentedLine
        .split('\n')
        .map((line) => `${indent}${line}`)
        .join('\n')
    : unindentedLine;
}

function findFormattedDirectBindingRange(model: MonacoEditor.ITextModel, varName: string, binding: InputBinding): BindingSourceRange | null {
  const expectedLine = buildDirectBindingLine(varName, binding);
  if (!expectedLine) return null;
  const formattedLine = formatGeneratedPythonCall(expectedLine);
  if (!formattedLine.includes('\n')) return null;

  const matches: BindingSourceRange[] = [];
  for (let i = 1; i <= model.getLineCount(); i++) {
    const expectedLines = applyLeadingIndentFromLine(model.getLineContent(i), formattedLine).split('\n');
    const endLine = i + expectedLines.length - 1;
    if (endLine > model.getLineCount()) continue;
    const sourceSpan = expectedLines.map((_, index) => model.getLineContent(i + index)).join('\n');
    if (sourceSpan === expectedLines.join('\n')) matches.push({ startLine: i, endLine });
  }

  const anchoredLine = binding.sourceLineNumber;
  if (anchoredLine !== undefined) {
    const anchoredMatch = matches.find((match) => anchoredLine >= match.startLine && anchoredLine <= match.endLine);
    if (anchoredMatch) return anchoredMatch;
  }
  return matches[0] ?? null;
}

/** Indentation-enclosing block headers from module level down to `lineNumber` (Python-style). */
function getPythonBlockScopeKey(model: MonacoEditor.ITextModel, lineNumber: number): string {
  const lineIndent = leadingWhitespaceLength(model.getLineContent(lineNumber));
  const headers: string[] = [];
  let enclosingIndent = lineIndent;

  for (let i = lineNumber - 1; i >= 1; i--) {
    const line = model.getLineContent(i);
    const trimmed = line.trim();
    if (!trimmed || trimmed.startsWith('#')) continue;
    const indent = leadingWhitespaceLength(line);
    if (indent < enclosingIndent) {
      headers.unshift(`${i}:${trimmed}`);
      enclosingIndent = indent;
    }
  }
  return headers.join('>');
}

/** True only when matches are the same line text in the same block scope (not separate if/else bodies). */
function matchingLinesAreCollapsibleDuplicates(model: MonacoEditor.ITextModel, lineNumbers: number[]): boolean {
  if (lineNumbers.length <= 1) return true;
  const firstLine = model.getLineContent(lineNumbers[0]);
  const firstScope = getPythonBlockScopeKey(model, lineNumbers[0]);
  return lineNumbers.every(
    (lineNumber) => model.getLineContent(lineNumber) === firstLine && getPythonBlockScopeKey(model, lineNumber) === firstScope
  );
}

function matchingLinesShareTrimmedContent(model: MonacoEditor.ITextModel, lineNumbers: number[]): boolean {
  if (lineNumbers.length <= 1) return true;
  const firstLine = model.getLineContent(lineNumbers[0]).trim();
  return lineNumbers.every((lineNumber) => model.getLineContent(lineNumber).trim() === firstLine);
}

function selectPrimaryMatchingLine(model: MonacoEditor.ITextModel, varName: string, binding: InputBinding, lineNumbers: number[]): number {
  const anchoredLine = binding.sourceLineNumber;
  if (anchoredLine !== undefined && lineNumbers.includes(anchoredLine)) {
    return anchoredLine;
  }
  const expectedLine = buildDirectBindingLine(varName, binding);
  if (expectedLine) {
    const exactMatch = lineNumbers.find((lineNumber) => model.getLineContent(lineNumber) === expectedLine);
    if (exactMatch !== undefined) return exactMatch;
  }
  return lineNumbers.reduce((best, current) =>
    leadingWhitespaceLength(model.getLineContent(current)) < leadingWhitespaceLength(model.getLineContent(best)) ? current : best
  );
}

/** Collapse only exact duplicate lines; trim-equal lines in different scopes edit a single primary line. */
function resolveMatchingLinesForEdit(model: MonacoEditor.ITextModel, varName: string, binding: InputBinding, matchingLines: number[]): number[] {
  if (matchingLines.length <= 1) return matchingLines;
  if (matchingLinesAreCollapsibleDuplicates(model, matchingLines)) return matchingLines;
  if (matchingLinesShareTrimmedContent(model, matchingLines)) {
    return [selectPrimaryMatchingLine(model, varName, binding, matchingLines)];
  }
  return matchingLines;
}

function findFirstMatchingRange(model: MonacoEditor.ITextModel, varName: string, binding: InputBinding): BindingSourceRange | null {
  if (isUnsupportedInputBinding(binding)) {
    return matchUnsupportedRawSpan(model, binding);
  }

  const matches = collectMatchingLineNumbers(model, varName, binding);
  if (matches.length === 0) return null;
  if (matches.length === 1) return { startLine: matches[0], endLine: matches[0] };
  if (matchingLinesAreCollapsibleDuplicates(model, matches)) {
    return { startLine: matches[0], endLine: matches[0] };
  }
  if (matchingLinesShareTrimmedContent(model, matches)) {
    const primaryLine = selectPrimaryMatchingLine(model, varName, binding, matches);
    return { startLine: primaryLine, endLine: primaryLine };
  }
  return null;
}

function deletedLineNumbersFromRange(range: BindingSourceRange): Set<number> {
  return new Set(Array.from({ length: range.endLine - range.startLine + 1 }, (_, index) => range.startLine + index));
}

function hasOtherMappings(
  model: MonacoEditor.ITextModel,
  varName: string,
  deletedLineNumbers: ReadonlySet<number>,
  includeAxisMappings: boolean
): boolean {
  const directRegex = anyDirectMappingRegex(varName);
  const axisRegex = includeAxisMappings ? buildControllerAnyAxisMappingRegex(varName) : null;
  const axisGetterRegex = includeAxisMappings ? controllerAxisGetterRegex(varName) : null;
  for (let i = 1; i <= model.getLineCount(); i++) {
    if (deletedLineNumbers.has(i)) continue;
    const line = model.getLineContent(i);
    if (directRegex.test(line) || axisRegex?.test(line) || axisGetterRegex?.test(line)) return true;
  }
  return false;
}

const TOP_LEVEL_VAR_ASSIGN_RE = /^\s*([a-zA-Z_][\w]*)\s*(?::\s*[^=]+?)?=/;

/** Panel declaration in effect immediately before the deleted mapping line, or null when none. */
function resolvePanelDeclarationLineIndexForDeletedMapping(
  lines: string[],
  varName: string,
  className: 'ActionController' | 'ActionDriveBasePanel',
  deletedLineNumbers: ReadonlySet<number>
): number | null {
  if (deletedLineNumbers.size === 0) return null;
  const mappingLineIndex0 = Math.min(...deletedLineNumbers) - 1;
  let effectiveDeclLineIndex: number | null = null;

  for (let i = 0; i < mappingLineIndex0; i++) {
    const line = lines[i] ?? '';
    if (!isTopLevelCodeLine(line)) continue;
    const constructorMatch = line.match(VAR_ASSIGN_RE);
    if (constructorMatch?.[1] === varName) {
      effectiveDeclLineIndex = constructorMatch[2] === className ? i : null;
      continue;
    }
    const assignMatch = line.match(TOP_LEVEL_VAR_ASSIGN_RE);
    if (assignMatch?.[1] === varName) {
      effectiveDeclLineIndex = null;
    }
  }

  return effectiveDeclLineIndex;
}

function deleteAssignmentIfLastMapping(
  model: MonacoEditor.ITextModel,
  edits: MonacoEditor.IIdentifiedSingleEditOperation[],
  varName: string,
  deletedLineNumbers: ReadonlySet<number>,
  className: 'ActionController' | 'ActionDriveBasePanel',
  includeAxisMappings: boolean
) {
  if (hasOtherMappings(model, varName, deletedLineNumbers, includeAxisMappings)) return;
  const lines = getTextModelLines(model);
  const declLineIndex = resolvePanelDeclarationLineIndexForDeletedMapping(lines, varName, className, deletedLineNumbers);
  if (declLineIndex === null) return;
  const endLineIndex = findConstructorCallEndLineIndex(lines, declLineIndex);
  edits.push({ range: new Range(declLineIndex + 1, 1, endLineIndex + 2, 1), text: '', forceMoveMarkers: true });
}

function executeEdits(
  editor: MonacoEditor.IStandaloneCodeEditor,
  source: string,
  edits: MonacoEditor.IIdentifiedSingleEditOperation[]
): CodeMutationResult {
  if (!edits.length) return { ok: true, changed: false };
  const targetEditor = getMainProgramEditorProxy(editor) ?? editor;
  targetEditor.pushUndoStop();
  targetEditor.executeEdits(source, edits);
  removeEmptyInputMappingsRegionIfNeeded(targetEditor);
  targetEditor.pushUndoStop();
  targetEditor.focus();
  return { ok: true, changed: true };
}

function insertControllerBinding(editor: MonacoEditor.IStandaloneCodeEditor, binding: InputBinding): CodeMutationResult {
  const targetEditor = getMainProgramEditorProxy(editor) ?? editor;
  const model = targetEditor.getModel();
  if (!model) return { ok: false, reason: 'No editor model available.' };
  ensureControllerExists(targetEditor);
  const nextModel = targetEditor.getModel();
  if (!nextModel) return { ok: false, reason: 'No editor model available after ensuring controller.' };
  const controllerVarName = resolveControllerVarName(nextModel);
  const newLine = buildFormattedDirectBindingLine(controllerVarName, binding);
  if (!newLine) return { ok: true, changed: false };
  const insertLine = getInsertLineForInputPanelBinding(nextModel, controllerVarName, 'controller', binding);
  return executeEdits(targetEditor, 'controller.mapping', [
    {
      range: new Range(insertLine, 1, insertLine, 1),
      text: newLine + '\n',
      forceMoveMarkers: true,
    },
  ]);
}

function insertDrivebaseBinding(editor: MonacoEditor.IStandaloneCodeEditor, binding: InputBinding): CodeMutationResult {
  const targetEditor = getMainProgramEditorProxy(editor) ?? editor;
  const model = targetEditor.getModel();
  if (!model) return { ok: false, reason: 'No editor model available.' };
  ensureDrivebasePanelExists(targetEditor);
  const nextModel = targetEditor.getModel();
  if (!nextModel) return { ok: false, reason: 'No editor model available after ensuring drivebase panel.' };
  const drivebaseVarName = resolveDrivebasePanelVarName(nextModel);
  const newLine = buildFormattedDirectBindingLine(drivebaseVarName, binding);
  if (!newLine) return { ok: true, changed: false };
  const insertLine = getInsertLineForInputPanelBinding(nextModel, drivebaseVarName, 'drivebase', binding);
  return executeEdits(targetEditor, 'drivebase.mapping', [
    {
      range: new Range(insertLine, 1, insertLine, 1),
      text: newLine + '\n',
      forceMoveMarkers: true,
    },
  ]);
}

export function deleteInputBindingFromCode(editor: MonacoEditor.IStandaloneCodeEditor, binding: InputBinding): CodeMutationResult {
  const targetEditor = getMainProgramEditorProxy(editor) ?? editor;
  const model = targetEditor.getModel();
  if (!model) return { ok: false, reason: 'No editor model available.' };
  const isDrivebase = binding.device === 'drivebase';
  const varName = isDrivebase ? resolveDrivebasePanelVarName(model) : resolveControllerVarName(model);

  if (isUnsupportedInputBinding(binding)) {
    const existingRange = matchUnsupportedRawSpan(model, binding);
    if (!existingRange) return { ok: false, reason: 'Unsupported mapping source could not be matched for deletion.' };
    const edits: MonacoEditor.IIdentifiedSingleEditOperation[] = [
      { range: new Range(existingRange.startLine, 1, existingRange.endLine + 1, 1), text: '', forceMoveMarkers: true },
    ];
    deleteAssignmentIfLastMapping(
      model,
      edits,
      varName,
      deletedLineNumbersFromRange(existingRange),
      isDrivebase ? 'ActionDriveBasePanel' : 'ActionController',
      !isDrivebase
    );
    return executeEdits(targetEditor, isDrivebase ? 'drivebase.mapping.delete' : 'controller.mapping.delete', edits);
  }

  const formattedRange = findFormattedDirectBindingRange(model, varName, binding);
  const existingRange = formattedRange ?? findFirstMatchingRange(model, varName, binding);
  if (!existingRange) return { ok: true, changed: false };
  const linesToDelete = formattedRange
    ? [...deletedLineNumbersFromRange(formattedRange)]
    : resolveMatchingLinesForEdit(model, varName, binding, collectMatchingLineNumbers(model, varName, binding));
  const edits: MonacoEditor.IIdentifiedSingleEditOperation[] = [...linesToDelete]
    .sort((left, right) => right - left)
    .map((lineNumber) => ({
      range: new Range(lineNumber, 1, lineNumber + 1, 1),
      text: '',
      forceMoveMarkers: true,
    }));
  deleteAssignmentIfLastMapping(
    model,
    edits,
    varName,
    new Set(linesToDelete),
    isDrivebase ? 'ActionDriveBasePanel' : 'ActionController',
    !isDrivebase
  );
  return executeEdits(targetEditor, isDrivebase ? 'drivebase.mapping.delete' : 'controller.mapping.delete', edits);
}

export function upsertInputBindingInCode(
  editor: MonacoEditor.IStandaloneCodeEditor,
  binding: InputBinding,
  previousBinding?: InputBinding | null
): CodeMutationResult {
  const targetEditor = getMainProgramEditorProxy(editor) ?? editor;
  if (previousBinding) {
    const replaceResult = replaceBindingInCode(targetEditor, previousBinding, binding);
    if (replaceResult.ok) return replaceResult;
    const deleteResult = deleteInputBindingFromCode(targetEditor, previousBinding);
    if (!deleteResult.ok) return deleteResult;
  }
  if (binding.device === 'drivebase') return insertDrivebaseBinding(targetEditor, binding);
  return insertControllerBinding(targetEditor, binding);
}

function bindingIdentityKey(binding: InputBinding): string {
  return `${String(binding.device || '')}|${String(binding.button || '')}|${String(binding.kind || '')}`;
}

function bindingButtonKey(binding: InputBinding): string {
  return `${String(binding.device || '')}|${String(binding.button || '')}`;
}

function groupBindingsByKey(bindings: InputBinding[], keyFn: (binding: InputBinding) => string): Map<string, InputBinding[]> {
  const groups = new Map<string, InputBinding[]>();
  for (const binding of bindings) {
    const key = keyFn(binding);
    const group = groups.get(key);
    if (group) group.push(binding);
    else groups.set(key, [binding]);
  }
  return groups;
}

function pairSingleBindingReplacements(
  deletions: InputBinding[],
  insertions: InputBinding[],
  keyFn: (binding: InputBinding) => string
): {
  pairs: Array<[InputBinding, InputBinding]>;
  remainingDeletions: InputBinding[];
  remainingInsertions: InputBinding[];
} {
  const pairs: Array<[InputBinding, InputBinding]> = [];
  const deleteByKey = groupBindingsByKey(deletions, keyFn);
  const insertByKey = groupBindingsByKey(insertions, keyFn);
  const remainingDeletions: InputBinding[] = [];
  const remainingInsertions: InputBinding[] = [];

  for (const [key, deleteGroup] of deleteByKey) {
    const insertGroup = insertByKey.get(key) ?? [];
    if (deleteGroup.length === 1 && insertGroup.length === 1) {
      pairs.push([deleteGroup[0], insertGroup[0]]);
      insertByKey.delete(key);
    } else {
      remainingDeletions.push(...deleteGroup);
    }
  }

  for (const insertGroup of insertByKey.values()) {
    remainingInsertions.push(...insertGroup);
  }

  return { pairs, remainingDeletions, remainingInsertions };
}

function replaceBindingInCode(
  editor: MonacoEditor.IStandaloneCodeEditor,
  previousBinding: InputBinding,
  nextBinding: InputBinding
): CodeMutationResult {
  const targetEditor = getMainProgramEditorProxy(editor) ?? editor;
  const model = targetEditor.getModel();
  if (!model) return { ok: false, reason: 'No editor model available.' };
  const isDrivebase = previousBinding.device === 'drivebase';
  const varName = isDrivebase ? resolveDrivebasePanelVarName(model) : resolveControllerVarName(model);
  const newLine = buildFormattedDirectBindingLine(varName, nextBinding);
  if (!newLine) return { ok: false, reason: 'Could not format replacement binding line.' };
  const formattedRange = findFormattedDirectBindingRange(model, varName, previousBinding);
  if (formattedRange) {
    const replacementLine = applyLeadingIndentFromLine(model.getLineContent(formattedRange.startLine), newLine);
    if (bindingLineNeedsRelocation(model, formattedRange.startLine, nextBinding)) {
      const device = isDrivebase ? 'drivebase' : 'controller';
      const deleteResult = executeEdits(targetEditor, isDrivebase ? 'drivebase.mapping.relocate.delete' : 'controller.mapping.relocate.delete', [
        {
          range: new Range(formattedRange.startLine, 1, formattedRange.endLine + 1, 1),
          text: '',
          forceMoveMarkers: true,
        },
      ]);
      if (!deleteResult.ok) return deleteResult;

      const nextModel = targetEditor.getModel();
      if (!nextModel) return { ok: false, reason: 'No editor model available after relocating binding.' };
      const insertLine = getInsertLineForInputPanelBinding(nextModel, varName, device, nextBinding);
      return executeEdits(targetEditor, isDrivebase ? 'drivebase.mapping.relocate.insert' : 'controller.mapping.relocate.insert', [
        {
          range: new Range(insertLine, 1, insertLine, 1),
          text: newLine + '\n',
          forceMoveMarkers: true,
        },
      ]);
    }
    const sourceSpan = Array.from({ length: formattedRange.endLine - formattedRange.startLine + 1 }, (_, index) =>
      model.getLineContent(formattedRange.startLine + index)
    ).join('\n');
    if (sourceSpan === replacementLine) return { ok: true, changed: false };
    return executeEdits(targetEditor, isDrivebase ? 'drivebase.mapping.replace' : 'controller.mapping.replace', [
      {
        range: new Range(formattedRange.startLine, 1, formattedRange.endLine + 1, 1),
        text: replacementLine + '\n',
        forceMoveMarkers: true,
      },
    ]);
  }
  const allMatchingLines = collectMatchingLineNumbers(model, varName, previousBinding);
  if (allMatchingLines.length === 0) return { ok: false, reason: 'Could not find existing binding line for in-place replacement.' };
  const linesToEdit = resolveMatchingLinesForEdit(model, varName, previousBinding, allMatchingLines);
  if (linesToEdit.length > 1 && !matchingLinesAreCollapsibleDuplicates(model, linesToEdit)) {
    return { ok: false, reason: 'Could not find existing binding line for in-place replacement.' };
  }
  const primaryLine = selectPrimaryMatchingLine(model, varName, previousBinding, linesToEdit);
  if (bindingLineNeedsRelocation(model, primaryLine, nextBinding)) {
    const device = isDrivebase ? 'drivebase' : 'controller';
    const deleteEdits: MonacoEditor.IIdentifiedSingleEditOperation[] = [...linesToEdit]
      .sort((left, right) => right - left)
      .map((lineNumber) => ({
        range: new Range(lineNumber, 1, lineNumber + 1, 1),
        text: '',
        forceMoveMarkers: true,
      }));
    const deleteResult = executeEdits(
      targetEditor,
      isDrivebase ? 'drivebase.mapping.relocate.delete' : 'controller.mapping.relocate.delete',
      deleteEdits
    );
    if (!deleteResult.ok) return deleteResult;

    const nextModel = targetEditor.getModel();
    if (!nextModel) return { ok: false, reason: 'No editor model available after relocating binding.' };
    const insertLine = getInsertLineForInputPanelBinding(nextModel, varName, device, nextBinding);
    return executeEdits(targetEditor, isDrivebase ? 'drivebase.mapping.relocate.insert' : 'controller.mapping.relocate.insert', [
      {
        range: new Range(insertLine, 1, insertLine, 1),
        text: newLine + '\n',
        forceMoveMarkers: true,
      },
    ]);
  }
  const replacementLine = applyLeadingIndentFromLine(model.getLineContent(primaryLine), newLine);
  if (linesToEdit.every((lineNumber) => model.getLineContent(lineNumber).trim() === newLine)) {
    return { ok: true, changed: false };
  }
  const edits: MonacoEditor.IIdentifiedSingleEditOperation[] = [...linesToEdit]
    .sort((left, right) => right - left)
    .map((lineNumber) => ({
      range: new Range(lineNumber, 1, lineNumber + 1, 1),
      text: lineNumber === primaryLine ? replacementLine + '\n' : '',
      forceMoveMarkers: true,
    }));
  return executeEdits(targetEditor, isDrivebase ? 'drivebase.mapping.replace' : 'controller.mapping.replace', edits);
}

export function replaceButtonInputBindingsInCode(
  editor: MonacoEditor.IStandaloneCodeEditor,
  previousBindings: InputBinding[],
  nextBindings: InputBinding[]
): CodeMutationResult {
  const targetEditor = getMainProgramEditorProxy(editor) ?? editor;
  const previousSignatures = new Set(previousBindings.map(inputBindingSignature));
  const nextDeduped = dedupeInputBindingsBySignature(nextBindings);
  const nextSignatures = new Set(nextDeduped.map(inputBindingSignature));

  const toDelete = previousBindings.filter((b) => !nextSignatures.has(inputBindingSignature(b)));
  const toInsert = nextDeduped.filter((b) => !previousSignatures.has(inputBindingSignature(b)));

  const sameKindPairing = pairSingleBindingReplacements(toDelete, toInsert, bindingIdentityKey);
  const triggerChangePairing = pairSingleBindingReplacements(
    sameKindPairing.remainingDeletions,
    sameKindPairing.remainingInsertions,
    bindingButtonKey
  );
  const inPlacePairs = [...sameKindPairing.pairs, ...triggerChangePairing.pairs];
  const remainingToDelete = triggerChangePairing.remainingDeletions;
  const remainingToInsert = triggerChangePairing.remainingInsertions;

  let changed = false;

  for (const [prev, next] of inPlacePairs) {
    const result = replaceBindingInCode(targetEditor, prev, next);
    if (!result.ok) {
      remainingToDelete.push(prev);
      remainingToInsert.push(next);
      continue;
    }
    changed = changed || result.changed;
  }

  for (const previousBinding of remainingToDelete) {
    const result = deleteInputBindingFromCode(targetEditor, previousBinding);
    if (!result.ok) return result;
    changed = changed || result.changed;
  }

  for (const nextBinding of remainingToInsert) {
    const result = upsertInputBindingInCode(targetEditor, nextBinding);
    if (!result.ok) return result;
    changed = changed || result.changed;
  }

  return { ok: true, changed };
}
